import React from 'react';
import MainPurpleMarker from '../images/marker/main-purple.jpg'
import LightPurpleMarker from '../images/marker/light-purple.jpg'
import MainGreenMarker from '../images/marker/main-green.jpg'
import LightGreenMarker from '../images/marker/light-green.jpg'
import MainOrangeMarker from '../images/marker/main-orange.jpg'
import LightOrangeMarker from '../images/marker/light-orange.jpg'

declare const L: any;

const Map: React.FunctionComponent<any> = ({ type, points, selectedPoint, onMarkerSelect, onLegendClick, showingCGI, setShowingCGI, showingMGI, setShowingMGI, showingLGI, setShowingLGI }) => {
  const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

  let initialZoom = 4;
  if (width < 768) {
    initialZoom = 3;
  }

  const initialLocation = [38.967243, -99.771556];
  const focusZoom = 12;
  const [mapInitialized, setMapInitialized] = React.useState(false);
  const [map, setMap] = React.useState<any>(null);
  const [selected, setSelected] = React.useState(null);
  // eslint-disable-next-line
  const [markers, setMarkers] = React.useState<any>(null);
  const [markersLayers, setMarkersLayers] = React.useState<any>(null);

  const createMakers = (points: any, cgiVisible: boolean, mgiVisible: boolean, lgiVisible: boolean) => {
    let filteredCGI = [];
    if (cgiVisible) {
      filteredCGI = points.filter((point: any) => point.isCGI);
    }

    let filteredMGI = [];
    if (mgiVisible) {
      filteredMGI = points.filter((point: any) => !point.isCGI && !point.isLGI);
    }

    let filteredLGI = [];
    if (lgiVisible) {
      filteredLGI = points.filter((point: any) => point.isLGI);
    }

    const markersLayer = L.markerClusterGroup({
      maxClusterRadius: 10,             // Minimal radius - only cluster overlapping or nearly overlapping markers
      spiderfyOnMaxZoom: true,          // Enable spider effect for overlapping markers at max zoom
      showCoverageOnHover: false,       // Cleaner appearance without coverage polygons
      animate: false,                   // Disable animations for better performance
      removeOutsideVisibleBounds: true, // Better performance with many markers
      zoomToBoundsOnClick: true,        // Zoom in when clicking clusters
      spiderfyDistanceMultiplier: 2,    // Increase distance between spiderfied markers
      spiderLegPolylineOptions: { weight: 1.5, color: '#666', opacity: 0.5 },
      iconCreateFunction: function(cluster: any) {
        const count = cluster.getChildCount();
        const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';
        return L.divIcon({
          html: '<div><span>' + count + '</span></div>',
          className: 'marker-cluster marker-cluster-' + size,
          iconSize: L.point(40, 40)
        });
      }
    });

    let CustomIcon = L.Icon.extend({
      options: {
        iconSize: [25, 25],
      }
    });


    // When showing all data (!type), put MGI at bottom so LGI and CGI appear on top
    const combinedPoints = !type ? 
      [...filteredMGI, ...filteredCGI, ...filteredLGI] :  // All data view: MGI bottom, LGI top
      [...filteredCGI, ...filteredMGI, ...filteredLGI];   // Filtered view: keep current order

    let markersArray = [];
    for (let i = 0; i < combinedPoints.length; i++) {
      const marker = L.marker(
        [combinedPoints[i].latitude, combinedPoints[i].longitude],
        {
          id: 'marker-' + i + '-' + (combinedPoints[i].isLGI ? 'lgi' : combinedPoints[i].isCGI ? 'cgi' : 'mgi'),
          data: combinedPoints[i],
          // Add z-index offset when showing all data to prioritize LGI and CGI
          zIndexOffset: !type ? (
            combinedPoints[i].isLGI ? 2000 :   // LGI on top
            combinedPoints[i].isCGI ? 1000 :   // CGI in middle
            0                                  // MGI at bottom
          ) : 0,
          icon: new CustomIcon({
            iconUrl: (combinedPoints[i].pilot_program === "Yes" || combinedPoints[i].yes === "Yes") ? 
              (combinedPoints[i].isLGI ? MainOrangeMarker :
               combinedPoints[i].isCGI ? MainGreenMarker : 
               MainPurpleMarker) : 
              (combinedPoints[i].isLGI ? LightOrangeMarker :
               combinedPoints[i].isCGI ? LightGreenMarker : 
               LightPurpleMarker)
          }),
        }
      )
        .on('click', (event: any) => {
          onMarkerSelect(event.target.options.data.id);
        });


      markersArray.push(marker);
      markersLayer.addLayer(marker);
    }

    setMarkers(markersArray);
    setMarkersLayers(markersLayer)
    map?.addLayer(markersLayer);
  }

  const initializeMap = (points: any) => {
    if (points && points.length > 0 && !mapInitialized) {
      setMapInitialized(true);
      let mymap = L.map('mapid').setView(initialLocation, initialZoom);
      mymap.scrollWheelZoom.disable()

      const esri = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas';
      L.tileLayer(`${esri}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`, {
        attribution: 'Powered by <a href="https://www.esri.com">Esri</a> | Esri, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxNativeZoom: 16,
        maxZoom: 19
      }).addTo(mymap);
      L.tileLayer(`${esri}/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}`, {
        maxNativeZoom: 16,
        maxZoom: 19
      }).addTo(mymap);

      const markersLayer = L.markerClusterGroup({
        maxClusterRadius: 10,             // Minimal radius - only cluster overlapping or nearly overlapping markers
        spiderfyOnMaxZoom: true,          // Enable spider effect for overlapping markers at max zoom
        showCoverageOnHover: false,       // Cleaner appearance without coverage polygons
        animate: false,                   // Disable animations for better performance
        removeOutsideVisibleBounds: true, // Better performance with many markers
        zoomToBoundsOnClick: true,        // Zoom in when clicking clusters
        spiderfyDistanceMultiplier: 2,    // Increase distance between spiderfied markers
        spiderLegPolylineOptions: { weight: 1.5, color: '#666', opacity: 0.5 },
        iconCreateFunction: function(cluster: any) {
          const count = cluster.getChildCount();
          const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';
          return L.divIcon({
            html: '<div><span>' + count + '</span></div>',
            className: 'marker-cluster marker-cluster-' + size,
            iconSize: L.point(40, 40)
          });
        }
      });
      mymap.addLayer(markersLayer);

      let CustomIcon = L.Icon.extend({
        options: {
          iconSize: [25, 25],
        }
      });

      let filteredPoints = points
      if (showingCGI && !showingMGI && !showingLGI) {
        filteredPoints = filteredPoints.filter((point: any) => point.isCGI);
      } else if (showingMGI && !showingCGI && !showingLGI) {
        filteredPoints = filteredPoints.filter((point: any) => !point.isCGI && !point.isLGI);
      } else if (showingLGI && !showingCGI && !showingMGI) {
        filteredPoints = filteredPoints.filter((point: any) => point.isLGI);
      }

      let markersArray = [];
      for (let i = 0; i < filteredPoints.length; i++) {
        const marker = L.marker(
          [filteredPoints[i].latitude, filteredPoints[i].longitude],
          {
            id: 'marker-' + i + '-' + (filteredPoints[i].isLGI ? 'lgi' : filteredPoints[i].isCGI ? 'cgi' : 'mgi'),
            data: filteredPoints[i],
            icon: new CustomIcon({
              iconUrl: (filteredPoints[i].pilot_program === "Yes" || filteredPoints[i].yes === "Yes") ? 
                (filteredPoints[i].isLGI ? MainOrangeMarker :
                 filteredPoints[i].isCGI ? MainGreenMarker : 
                 MainPurpleMarker) : 
                (filteredPoints[i].isLGI ? LightOrangeMarker :
                 filteredPoints[i].isCGI ? LightGreenMarker : 
                 LightPurpleMarker)
            }),
          }
        )
          .on('click', (event: any) => {
            onMarkerSelect(event.target.options.data.id);
          });

        markersArray.push(marker);
        markersLayer.addLayer(marker);
      }

      setMarkers(markersArray);
      setMarkersLayers(markersLayer)
      setMap(mymap);
    }
  };

  const focusPoint = () => {
    map?.setView([selectedPoint.latitude, selectedPoint.longitude], focusZoom);
  };

  const clearFocus = () => {
    map?.setView(initialLocation, initialZoom);
  };

  const showPoints = (pointType: string) => () => {
    markersLayers.clearLayers();
    map.removeLayer(markersLayers);
    map.removeLayer(markers);

    if (pointType === 'mgi') {
      setShowingMGI(type === 'mgi' ? true : !showingMGI);
    } else if (pointType === 'cgi') {
      setShowingCGI(type === 'cgi' ? true : !showingCGI);
    } else if (pointType === 'lgi') {
      setShowingLGI(type === 'lgi' ? true : !showingLGI);
    }
    onLegendClick(pointType);
  }

  React.useEffect(() => {
    initializeMap(points);
    if (selectedPoint) {
      setSelected(selectedPoint);
    } else {
      setSelected(null);
    }
    // eslint-disable-next-line
  }, [selectedPoint, points]);

  React.useEffect(() => {
    if (selected) {
      focusPoint();
    } else {
      clearFocus();
    }
    // eslint-disable-next-line
  }, [selected]);

  React.useEffect(() => {
    createMakers(points, showingCGI, showingMGI, showingLGI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingCGI, showingMGI, showingLGI]);

  return (
    <main className="mt-24 h-72 md:w-2/6 md:flex-1 md:mt-0 md:h-screen md:p-6 relative">
      <div className='legend absolute'>
        <ul>
          <li><div onClick={type !== 'mgi' ? showPoints('mgi') : undefined} className={showingMGI ? 'mgi' : ''}></div>Mayors</li>
          <li><div onClick={type !== 'cgi' ? showPoints('cgi') : undefined} className={showingCGI ? 'cgi' : ''}></div>County Officials</li>
          <li><div onClick={type !== 'lgi' ? showPoints('lgi') : undefined} className={showingLGI ? 'lgi' : ''}></div>Legislators</li>
        </ul>
      </div>
      <div id="mapid" className="w-full h-full"></div>
    </main>
  )
}

export default Map;